import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import omit from 'lodash.omit';

import { getMaxThumb } from '../../util/photo';
import { fileInputAccept } from '../../constants/photo-upload';
import PhotoUploadCache from '../product/PhotoUploadCache';
import Button from './Button';
import NativeActivityIndicator from './NativeActivityIndicator';
import TrashIcon from '../icons/TrashIcon';
import EditIcon from '../icons/EditIcon';

const propTypes = {
  id: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  onInputChange: PropTypes.func.isRequired,
  onImageDismiss: PropTypes.func.isRequired,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  image: PropTypes.shape({
    id: PropTypes.string,
    thumbnail_urls: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    ),
  }),
  notes: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  imageUploadStyle: PropTypes.oneOf(['primary', 'secondary']),
  children: PropTypes.node,
  labelProps: PropTypes.shape({
    className: PropTypes.string,
  }),
  customFileInputAccept: PropTypes.string,
  fileInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

const ImageUpload = ({
  id,
  loading,
  onInputChange,
  onImageDismiss,
  buttonText = 'Upload',
  className,
  image,
  notes = [],
  disabled,
  imageUploadStyle,
  labelProps,
  children,
  customFileInputAccept,
  fileInputRef,
}) => {
  const defaultInputRef = useRef();
  const inputRef = fileInputRef || defaultInputRef;

  const classes = classNames({
    'image-upload': true,
    'image-upload-secondary': imageUploadStyle === 'secondary',
    'is-disabled': disabled,
    [className]: !!className,
  });

  const labelClasses = classNames({
    'image-upload-label': true,
    [labelProps?.className]: !!labelProps?.className,
  });

  return (
    <div className={classes}>
      <div className="d-flex">
        <label
          htmlFor={id}
          className={labelClasses}
          {...omit(labelProps, 'className')}
        >
          {loading && <NativeActivityIndicator size="sm" />}

          {!loading && image && image.id && (
            <div className="image-upload-preview">
              <PhotoUploadCache
                id={image.id}
                thumbs={image.thumbnail_urls}
                render={thumbs => <img src={getMaxThumb(thumbs).url} alt="" />}
              />
            </div>
          )}

          {!loading &&
            (!image || !image.id) &&
            (children ?? (
              <Button className="image-upload-btn text-transform-none">
                {buttonText}
              </Button>
            ))}

          <input
            id={id}
            ref={inputRef}
            type="file"
            accept={customFileInputAccept ?? fileInputAccept}
            onChange={onInputChange}
            disabled={loading || disabled}
            value=""
          />
        </label>
        {image && image.id && !disabled && (
          <div className="image-upload-buttons">
            <Button
              onClick={() => inputRef.current.click()}
              className="image-upload-button"
              btnStyle="reset"
            >
              <EditIcon color="#0d84fe" size={18} className="mb-1" />
            </Button>
            <Button
              onClick={onImageDismiss}
              className="image-upload-button"
              btnStyle="reset"
            >
              <TrashIcon color="red" size={18} />
            </Button>
          </div>
        )}
      </div>
      {!!notes?.length && (
        <div className="image-upload-notes">
          {notes.map((note, idx) => (
            <span key={idx} className="image-upload-note">
              {note}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

ImageUpload.propTypes = propTypes;

export default ImageUpload;
